import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    applyColorScheme,
    COLOR_SCHEME_STORAGE_KEY,
    type ColorSchemePreference,
    DARK_COLOR_SCHEME_QUERY,
    isColorSchemePreference,
    type ResolvedColorScheme,
    readColorSchemePreference,
    readSystemColorScheme,
    resolveColorScheme,
    writeColorSchemePreference,
} from './color-scheme';

type ColorSchemeContextValue = {
    readonly colorScheme: ResolvedColorScheme;
    readonly preference: ColorSchemePreference;
    readonly setPreference: (preference: ColorSchemePreference) => void;
};

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);

export const ColorSchemeProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [preference, setPreferenceState] = useState(readColorSchemePreference);
    const [systemColorScheme, setSystemColorScheme] = useState(readSystemColorScheme);
    const colorScheme = resolveColorScheme(preference, systemColorScheme);

    useEffect(() => {
        applyColorScheme(colorScheme);
    }, [colorScheme]);

    useEffect(() => {
        const mediaQuery = globalThis.matchMedia(DARK_COLOR_SCHEME_QUERY);
        const updateSystemColorScheme = (): void => {
            const nextSystemColorScheme = mediaQuery.matches ? 'dark' : 'light';
            applyColorScheme(resolveColorScheme(preference, nextSystemColorScheme));
            setSystemColorScheme(nextSystemColorScheme);
        };

        mediaQuery.addEventListener('change', updateSystemColorScheme);

        return () => {
            mediaQuery.removeEventListener('change', updateSystemColorScheme);
        };
    }, [preference]);

    useEffect(() => {
        const updateStoredPreference = (event: StorageEvent): void => {
            if (event.key === COLOR_SCHEME_STORAGE_KEY) {
                const nextPreference = isColorSchemePreference(event.newValue) ? event.newValue : 'light';
                applyColorScheme(resolveColorScheme(nextPreference, readSystemColorScheme()));
                setPreferenceState(nextPreference);
            }
        };

        globalThis.addEventListener('storage', updateStoredPreference);

        return () => {
            globalThis.removeEventListener('storage', updateStoredPreference);
        };
    }, []);

    const setPreference = useCallback((nextPreference: ColorSchemePreference): void => {
        applyColorScheme(resolveColorScheme(nextPreference, readSystemColorScheme()));
        setPreferenceState(nextPreference);
        writeColorSchemePreference(nextPreference);
    }, []);

    const value = useMemo(() => ({ colorScheme, preference, setPreference }), [colorScheme, preference, setPreference]);

    return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
};

export const useColorScheme = (): ColorSchemeContextValue => {
    const context = useContext(ColorSchemeContext);

    if (context === null) {
        throw new Error('useColorScheme must be used within a ColorSchemeProvider');
    }

    return context;
};
